import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutDashboard, FileText, MoreHorizontal, X, BookOpen, Gamepad2, Heart, Users, Crown, Target, Brain, MessageCircle, ShoppingBag } from 'lucide-react';

const BottomNav = ({ user, userResults }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const mainNavItems = [
    { path: '/', icon: Home, label: 'Home', showAlways: true },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', requireAuth: true },
    { path: '/personalized', icon: FileText, label: 'Tests', requireAuth: true }
  ];

  const moreMenuItems = [
    { path: '/store', icon: ShoppingBag, label: 'Store', showAlways: true, gradient: 'from-emerald-500 to-green-600' },
    { path: '/courses', icon: BookOpen, label: 'Courses', requireAuth: true, gradient: 'from-blue-500 to-cyan-600' },
    { path: '/games', icon: Gamepad2, label: 'Games', requireAuth: true, requireResults: 2, gradient: 'from-pink-500 to-purple-600' },
    { path: '/couples', icon: Heart, label: 'Couples', requireAuth: true, gradient: 'from-pink-500 to-rose-600' },
    { path: '/community', icon: Users, label: 'Community', requireAuth: true, gradient: 'from-green-500 to-teal-600' },
    { path: '/challenges', icon: Target, label: 'Challenges', requireAuth: true, gradient: 'from-purple-500 to-indigo-600' },
    { path: '/emotions', icon: Brain, label: 'Emotions', requireAuth: true, gradient: 'from-indigo-500 to-purple-600' },
    { path: '/messages', icon: MessageCircle, label: 'Messages', requireAuth: true, gradient: 'from-blue-400 to-cyan-500' },
    { path: '/premium', icon: Crown, label: 'Premium', showAlways: true, premium: true, gradient: 'from-yellow-400 to-yellow-500' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setShowMoreMenu(false);
  };

  return (
    <>
      {/* Bottom Navigation Bar - Only show when logged in */}
      {user && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-rose-600 to-pink-600 md:hidden">
          <div className="max-w-screen-xl mx-auto px-2">
            <div className="flex justify-around items-center h-16">
              {mainNavItems.map((item) => {
                if (item.requireAuth && !user) return null;

                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex flex-col items-center justify-center flex-1 h-full transition-all relative ${
                      active 
                        ? 'text-white'
                        : 'text-rose-100'
                    }`}
                  >
                    {active && (
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1.5 rounded-b-full bg-white shadow-lg" />
                    )}
                    <Icon className={`w-7 h-7 mb-1 ${active ? 'scale-110 drop-shadow-lg' : ''}`} strokeWidth={active ? 3 : 2.5} />
                    <span className={`text-xs ${active ? 'font-bold' : 'font-semibold'}`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}

              {/* More Menu Button - with pulse animation to attract attention */}
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-all relative ${
                  showMoreMenu
                    ? 'text-white'
                    : 'text-rose-100 animate-pulse-scale'
                }`}
              >
                {showMoreMenu && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1.5 rounded-b-full bg-white shadow-lg" />
                )}
                {showMoreMenu ? (
                  <X className="w-7 h-7 mb-1" strokeWidth={3} />
                ) : (
                  <MoreHorizontal className="w-7 h-7 mb-1" strokeWidth={2.5} />
                )}
                <span className={`text-xs ${showMoreMenu ? 'font-bold' : 'font-semibold'}`}>
                  More
                </span>
              </button>
            </div>
          </div>
        </nav>
      )}

      {/* More Menu Overlay - Only show when logged in */}
      {user && showMoreMenu && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setShowMoreMenu(false)}
        />
      )}

      {/* More Menu Panel - Only show when logged in */}
      {user && showMoreMenu && (
        <div className="fixed bottom-16 left-0 right-0 z-50 bg-white dark:bg-gray-900 shadow-2xl md:hidden max-h-[70vh] overflow-y-auto">
          <div className="p-4 bg-gradient-to-b from-rose-50 to-white dark:from-gray-800 dark:to-gray-900">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <MoreHorizontal className="w-5 h-5 mr-2 text-rose-600 dark:text-rose-400" />
              More Options
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {moreMenuItems.map((item) => {
                if (item.requireAuth && !user) return null;
                if (item.requireResults && (!userResults || userResults.length < item.requireResults)) return null;

                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all shadow-lg hover:shadow-xl hover:scale-105 bg-gradient-to-br ${item.gradient} text-white ${
                      active ? 'scale-105 ring-2 ring-white ring-offset-2' : ''
                    }`}
                  >
                    <Icon className="w-8 h-8 mb-2 drop-shadow-lg" strokeWidth={2.5} />
                    <span className="text-xs text-center leading-tight font-semibold">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(BottomNav);
